import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { posthog } from '../lib/analytics/posthogClient';
import { supabase } from '../lib/supabase';
import { useUserAuthOptional } from './UserAuthContext';
import { useUserFavoritesSync } from '../hooks/useUserFavoritesSync';
import { requestLoginToSave } from '../utils/pendingSave';

/**
 * SavedEventsContext — Opzione B (hard wall) semantics.
 *
 * Public API surface unchanged (`savedIds`, `isSaved`, `toggleSaved`,
 * `savedCount`) but semantics have shifted:
 *  - When ANONYMOUS:
 *      • `isSaved(id)` always returns `false` (saves require login).
 *      • `toggleSaved(id)` does NOT mutate state; instead it records a
 *        pending intent and redirects to `/accedi`. UI stays "not saved".
 *      • Legacy `localStorage.fieravicino_saved_events` is no longer used
 *        as a live cache — it is read once by `useUserFavoritesSync` on
 *        first login (then cleared) for one-shot migration.
 *  - When AUTHENTICATED:
 *      • State is hydrated from `user_favorites` by `useUserFavoritesSync`.
 *      • `toggleSaved(id)` writes directly to DB (insert/delete) and
 *        mirrors in-memory state. No localStorage involvement.
 *  - On SIGNED_OUT: state cleared (no leftover from previous user).
 */

export interface SavedEventsContextType {
  savedIds: Set<string>;
  isSaved: (id: string) => boolean;
  toggleSaved: (id: string) => void;
  savedCount: number;
}

const SavedEventsContext = createContext<SavedEventsContextType | undefined>(undefined);

interface SavedEventsProviderProps {
  children: ReactNode;
}

export function SavedEventsProvider({ children }: SavedEventsProviderProps) {
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const { user, isLoading } = useUserAuthOptional();
  const navigate = useNavigate();
  const location = useLocation();

  // Wire DB <-> localStorage sync (idempotent per user.id).
  useUserFavoritesSync({ setSavedIds });

  const isSaved = useCallback(
    (id: string) => {
      // Race-condition guard: while the Supabase session is still resolving,
      // fall through to the in-memory set rather than returning false. This
      // prevents an unsaved→saved flicker on hydration. `savedIds` is hydrated
      // by `useUserFavoritesSync` only after login resolves, so this still
      // returns false correctly for truly anonymous users.
      if (isLoading) return savedIds.has(id);
      // Post-Opzione B: anonymous users cannot be "saved" — always false.
      if (!user) return false;
      return savedIds.has(id);
    },
    [savedIds, user, isLoading]
  );

  const toggleSaved = useCallback(
    (id: string) => {
      // Race-condition guard: ignore clicks while the session is still resolving.
      // Without this, a click in the ~100–800ms window between mount and session
      // resolution would incorrectly take the anonymous path and redirect to /accedi
      // even for logged-in users.
      if (isLoading) return;

      // Anonymous: hard wall — defer save until login.
      if (!user) {
        // T018: capture intent before redirect (anonymous always implies "saved" intent).
        posthog.capture('favorite_toggled', {
          event_id: id,
          new_state: 'saved',
          auth_state: 'anon',
        });
        // Pass current location so AuthModal can overlay on desktop.
        requestLoginToSave(id, navigate, location);
        return;
      }

      const userId = user.id;
      const currentlySaved = savedIds.has(id);
      const nowSaved = !currentlySaved;

      // Optimistic state update
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (currentlySaved) next.delete(id);
        else next.add(id);
        return next;
      });

      // T018: capture after optimistic state change.
      posthog.capture('favorite_toggled', {
        event_id: id,
        new_state: nowSaved ? 'saved' : 'unsaved',
        auth_state: 'user',
      });

      // Persist to DB; revert on failure.
      (async () => {
        try {
          if (currentlySaved) {
            const { error } = await supabase
              .from('user_favorites')
              .delete()
              .eq('user_id', userId)
              .eq('event_id', id);
            if (error) throw error;
          } else {
            const { error } = await supabase
              .from('user_favorites')
              .insert({ user_id: userId, event_id: id });
            if (error) throw error;
          }
        } catch (err) {
          console.warn('[SavedEventsContext] toggle persist failed:', err);
          // Revert optimistic update
          setSavedIds((prev) => {
            const next = new Set(prev);
            if (currentlySaved) next.add(id);
            else next.delete(id);
            return next;
          });
        }
      })();
    },
    [user, isLoading, savedIds, navigate, location]
  );

  const savedCount = useMemo(() => savedIds.size, [savedIds]);

  const value = useMemo<SavedEventsContextType>(
    () => ({
      savedIds,
      isSaved,
      toggleSaved,
      savedCount,
    }),
    [savedIds, isSaved, toggleSaved, savedCount]
  );

  return <SavedEventsContext.Provider value={value}>{children}</SavedEventsContext.Provider>;
}

export function useSavedEventsContext(): SavedEventsContextType {
  const context = useContext(SavedEventsContext);
  if (context === undefined) {
    throw new Error('useSavedEventsContext must be used within a SavedEventsProvider');
  }
  return context;
}
